import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE } from "../../../../config";
import { openPdfInNewTab } from "../../../../utils/pdfOpener";

// use centralized API_BASE from config

// Turn any pasted URL into a storage object path (no bucket, no domain, no leading slash)
const toObjectPath = (value) => {
  if (!value) return value;
  let v = value.trim();

  // If bucket accidentally included, drop it (we use one bucket server-side)
  v = v.replace(/^exam[_-]?papers\//i, "");

  // Remove leading slashes
  v = v.replace(/^\/+/, "");
  return v;
};

const AdminAQA = () => {
  // State for storing AQA papers
  const [aqaPapers, setAqaPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for form input when adding a new paper
  const [newPaper, setNewPaper] = useState({
    year: "",
    month: "June",
    level: "Foundation",
    paper_number: "",
    file_url: "",
    ms_url: "",
  });

  // Fetch AQA exam papers on mount
  useEffect(() => {
    const fetchAqaPapers = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/exams/exam-papers`);
        const all = response.data?.data || [];
        const filtered = all.filter(
          (p) => (p.exam_board || "").toLowerCase() === "aqa"
        );
        setAqaPapers(filtered);
        setError(null);
      } catch (err) {
        console.error("Error fetching AQA exam papers:", err);
        setError(err.response?.data?.error || "Failed to fetch AQA exam papers.");
      } finally {
        setLoading(false);
      }
    };

    fetchAqaPapers();
  }, []);

  // Handler to add a new exam paper
  const handleAddPaper = async () => {
    if (
      !newPaper.year ||
      !newPaper.month ||
      !newPaper.level ||
      !newPaper.paper_number ||
      !newPaper.file_url
    ) {
      toast.error("Please fill in all fields.");
      return;
    }

    const yearNum = Number(newPaper.year);
    const paperNum = Number(newPaper.paper_number);

    try {
      await axios.post(`${API_BASE}/api/exams/exam-papers`, {
        exam_board: "AQA",
        year: Number.isNaN(yearNum) ? newPaper.year : yearNum,
        month: newPaper.month,
        level: newPaper.level,
        paper_number: Number.isNaN(paperNum) ? newPaper.paper_number : paperNum,
        file_url: toObjectPath(newPaper.file_url),
        ms_url: newPaper.ms_url ? toObjectPath(newPaper.ms_url) : null,
      });

      toast.success("Exam paper added successfully.");

      // Reset form fields
      setNewPaper({
        year: "",
        month: "June",
        level: "Foundation",
        paper_number: "",
        file_url: "",
        ms_url: "",
      });

      // Refresh paper list
      const response = await axios.get(`${API_BASE}/api/exams/exam-papers`);
      const all = response.data?.data || [];
      const filtered = all.filter(
        (p) => (p.exam_board || "").toLowerCase() === "aqa"
      );
      setAqaPapers(filtered);
      setError(null);
    } catch (err) {
      console.error("Error adding exam paper:", err);
      toast.error(`Failed to add exam paper: ${err.response?.data?.error || err.message}`);
    }
  };

  // Handler to delete an exam paper by ID
  const handleDeletePaper = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this paper?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE}/api/exams/exam-papers/${id}`);
      setAqaPapers((prev) => prev.filter((paper) => paper.id !== id));
      toast.success("Exam paper deleted successfully.");
    } catch (err) {
      console.error("Error deleting exam paper:", err);
      toast.error(`Failed to delete exam paper: ${err.response?.data?.error || err.message}`);
    }
  };

  // Handler to generate a signed URL and open the file in a new tab
  const handleViewFile = async (filePath) => {
    try {
      const response = await axios.get(`${API_BASE}/api/exams/signed-url`, {
        params: { file_path: filePath },
      });
      const signedUrl = response.data.signedUrl;
      openPdfInNewTab(signedUrl);
    } catch (error) {
      console.error("Error generating signed URL:", error);
      toast.error(`Failed to fetch the file. ${error.response?.data?.error || error.message}`);
    }
  };

  if (loading) return <div className="text-center py-20 text-teal-600">Loading...</div>;
  if (error) return <div className="text-center py-20 text-red-500">{error}</div>;

  // Separate papers by level
  const higherPapers = aqaPapers.filter((paper) => (paper.level || "").toLowerCase() === "higher");
  const foundationPapers = aqaPapers.filter((paper) => (paper.level || "").toLowerCase() === "foundation");

  return (
    <div className="container mx-auto py-10 px-6 lg:px-12">
      <h1 className="text-4xl font-bold text-teal-600 mb-8 text-center">
        Manage AQA Exam Papers
      </h1>

      <h2 className="text-3xl font-bold text-teal-600 mt-10 mb-4">Add New Paper</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-100 p-6 rounded-lg shadow-lg mb-10">
        <input
          type="text"
          placeholder="Year"
          value={newPaper.year}
          onChange={(e) => setNewPaper({ ...newPaper, year: e.target.value })}
          className="p-3 border rounded-lg shadow-sm"
        />
        <select
          value={newPaper.month}
          onChange={(e) => setNewPaper({ ...newPaper, month: e.target.value })}
          className="p-3 border rounded-lg shadow-sm"
        >
          <option value="June">June</option>
          <option value="November">November</option>
        </select>
        <select
          value={newPaper.level}
          onChange={(e) => setNewPaper({ ...newPaper, level: e.target.value })}
          className="p-3 border rounded-lg shadow-sm"
        >
          <option value="Higher">Higher</option>
          <option value="Foundation">Foundation</option>
        </select>
        <input
          type="text"
          placeholder="Paper Number"
          value={newPaper.paper_number}
          onChange={(e) => setNewPaper({ ...newPaper, paper_number: e.target.value })}
          className="p-3 border rounded-lg shadow-sm"
        />
        <input
          type="text"
          placeholder="Path (e.g. AQA/2023/June/Foundation/8300-1F-QP.pdf)"
          value={newPaper.file_url}
          onChange={(e) => setNewPaper({ ...newPaper, file_url: e.target.value })}
          className="p-3 border rounded-lg shadow-sm col-span-2"
        />
        <input
          type="text"
          placeholder="Mark Scheme Path (optional, e.g. AQA/2023/June/Foundation/8300-1F-MS.pdf)"
          value={newPaper.ms_url}
          onChange={(e) => setNewPaper({ ...newPaper, ms_url: e.target.value })}
          className="p-3 border rounded-lg shadow-sm col-span-2"
        />
        <button
          onClick={handleAddPaper}
          className="px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 col-span-2"
        >
          Add Paper
        </button>
      </div>

      <h2 className="text-2xl font-bold text-teal-500 mb-4">Foundation Level</h2>
      <div className="overflow-x-auto shadow-lg rounded-lg mb-10">
        <table className="table-auto w-full border-collapse">
          <thead>
            <tr className="bg-teal-500 text-white">
              <th className="px-6 py-3">Year</th>
              <th className="px-6 py-3">Month</th>
              <th className="px-6 py-3">Level</th>
              <th className="px-6 py-3">Paper Number</th>
              <th className="px-6 py-3 text-center">Question Paper</th>
              <th className="px-6 py-3 text-center">Mark Scheme</th>
              <th className="px-6 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {foundationPapers.map((paper, index) => (
              <tr
                key={paper.id}
                className={
                  Math.floor(index / 3) % 2 === 0
                    ? "bg-gray-50 hover:bg-gray-100"
                    : "bg-white hover:bg-gray-50"
                }
              >
                <td className="border px-6 py-3">{paper.year}</td>
                <td className="border px-6 py-3">{paper.month}</td>
                <td className="border px-6 py-3">{paper.level}</td>
                <td className="border px-6 py-3">Paper {paper.paper_number}</td>
                <td className="border px-6 py-3 text-center">
                  <button
                    onClick={() => handleViewFile(paper.file_url)}
                    className="text-teal-500 font-medium hover:underline"
                  >
                    View Paper
                  </button>
                </td>
                <td className="border px-6 py-3 text-center">
                  {paper.ms_url ? (
                    <button
                      onClick={() => handleViewFile(paper.ms_url)}
                      className="text-green-500 font-medium hover:underline"
                    >
                      View Mark Scheme
                    </button>
                  ) : (
                    <span className="text-gray-400">No Mark Scheme</span>
                  )}
                </td>
                <td className="border px-6 py-3 text-center">
                  <button
                    onClick={() => handleDeletePaper(paper.id)}
                    className="text-red-500 font-medium hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="text-2xl font-bold text-teal-500 mb-4">Higher Level</h2>
      <div className="overflow-x-auto shadow-lg rounded-lg mb-10">
        <table className="table-auto w-full border-collapse">
          <thead>
            <tr className="bg-teal-500 text-white">
              <th className="px-6 py-3">Year</th>
              <th className="px-6 py-3">Month</th>
              <th className="px-6 py-3">Level</th>
              <th className="px-6 py-3">Paper Number</th>
              <th className="px-6 py-3 text-center">Question Paper</th>
              <th className="px-6 py-3 text-center">Mark Scheme</th>
              <th className="px-6 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {higherPapers.map((paper, index) => (
              <tr
                key={paper.id}
                className={
                  Math.floor(index / 3) % 2 === 0
                    ? "bg-gray-50 hover:bg-gray-100"
                    : "bg-white hover:bg-gray-50"
                }
              >
                <td className="border px-6 py-3">{paper.year}</td>
                <td className="border px-6 py-3">{paper.month}</td>
                <td className="border px-6 py-3">{paper.level}</td>
                <td className="border px-6 py-3">Paper {paper.paper_number}</td>
                <td className="border px-6 py-3 text-center">
                  <button
                    onClick={() => handleViewFile(paper.file_url)}
                    className="text-teal-500 font-medium hover:underline"
                  >
                    View Paper
                  </button>
                </td>
                <td className="border px-6 py-3 text-center">
                  {paper.ms_url ? (
                    <button
                      onClick={() => handleViewFile(paper.ms_url)}
                      className="text-green-500 font-medium hover:underline"
                    >
                      View Mark Scheme
                    </button>
                  ) : (
                    <span className="text-gray-400">No Mark Scheme</span>
                  )}
                </td>
                <td className="border px-6 py-3 text-center">
                  <button
                    onClick={() => handleDeletePaper(paper.id)}
                    className="text-red-500 font-medium hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAQA;